import { type AxiosInstance } from 'axios';

import { useUserStore } from '@store/use-user-store';
import { navigateTo } from '@utils/navigation';
import { ROUTES } from '@constants/routes';

import { REFRESH_TOKEN_URL } from './Api';

export function isTokenExpired(token: string): boolean {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const now = Date.now() / 1000;
    const secsLeft = Math.floor(payload.exp - now);
    console.log(`Token expires in: ${secsLeft}s`);
    return secsLeft < 0;
  } catch {
    return true;
  }
}

function setupAxios(axiosInstance: AxiosInstance): void {
  const setHeaders = (config: { headers: Record<string, string> }) => {
    const { accessToken, selectedLang } = useUserStore.getState();
    config.headers['x-language'] = selectedLang || 'en';
    if (accessToken) {
      config.headers['Authorization'] = `Bearer ${accessToken}`;
    }
    return config;
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  axiosInstance.interceptors.request.use((config: any) => setHeaders(config));

  let isAlreadyFetchingAccessToken = false;
  let subscribers: Array<(token: string) => void> = [];

  function onAccessTokenFetched(accessToken: string) {
    subscribers = subscribers.filter((callback) => callback(accessToken));
  }

  function addSubscriber(callback: (token: string) => void) {
    subscribers.push(callback);
  }

  axiosInstance.interceptors.response.use(
    (response) => response,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (err: any) => {
      if (
        err?.response?.status === 401 &&
        err?.response?.data?.errorCode === 4
      ) {
        try {
          const { refreshToken, selectedLang } = useUserStore.getState();
          const originalRequest = err.config;

          if (!isAlreadyFetchingAccessToken) {
            isAlreadyFetchingAccessToken = true;
            fetch(REFRESH_TOKEN_URL, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'x-language': selectedLang || 'en',
                Authorization: 'Bearer ' + refreshToken
              }
            })
              .then((response) => response.json())
              .then((data: { accessToken: string }) => {
                onAccessTokenFetched(data.accessToken);
                isAlreadyFetchingAccessToken = false;
              })
              .catch(() => {
                navigateTo(ROUTES.LOGOUT);
              });
          }

          return new Promise<unknown>((resolve) => {
            addSubscriber((accessToken: string) => {
              originalRequest.headers['Authorization'] =
                'Bearer ' + accessToken;
              axiosInstance.defaults.headers.common['Authorization'] =
                'Bearer ' + accessToken;
              useUserStore.getState().changeAccessToken(accessToken);
              resolve(axiosInstance(originalRequest));
            });
          });
        } catch {
          navigateTo(ROUTES.LOGOUT);
        } finally {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          axiosInstance.interceptors.request.use((config: any) =>
            setHeaders(config)
          );
        }
      } else {
        return Promise.reject(err);
      }
    }
  );
}

export default setupAxios;
